import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Input, Select, Button, Space, Typography, message, Divider, Tag, ConfigProvider, Tooltip, Tabs } from "antd";
import useUserContext from "../../../hooks/useUserContext";
import { CreateV2Template, GetV2Template, UpdateV2Template } from "../../../api/v2/templates";
import Page from "../../../components/page/Page";
import Loader from "../../../components/loader/Loader";
import LanguagePicker from "../../../components/forms/languagePicker/LanguagePicker";
import { LANGUAGES } from "../../../utils/languages";
import WhatsappScreen from "../../../assets/images/whatsapp-screen-2.png";
import WhatsappWallpaper from "../../../assets/images/whatsapp-default-wallpaper.png";
import StoreWhatsappImage from "../../../assets/images/store-whatsapp-image.png";
import DefaultPreviewImage from "../../../assets/images/sunglasses.png";
import TemplateIconTextButton from "../../../assets/icons/template-icons/template-icon-text-button.png";
import TemplateIconMenu from "../../../assets/icons/template-icons/template-icon-menu.png";
import TemplateIconImageText from "../../../assets/icons/template-icons/template-icon-image-text.png";
import TemplateIconImageTextIcon from "../../../assets/icons/template-icons/template-icon-image-text-icon.png";
import TemplateIconCarousel from "../../../assets/icons/template-icons/template-icon-carousel.png";
import TemplateIconText from "../../../assets/icons/template-icons/template-icon-text.png";
import "./template-builder-page.css";

const { Text } = Typography;

type LayoutType = "text" | "image_text" | "text_buttons" | "image_text_buttons" | "menu" | "carousel";

type ImageSource = "meta" | "static";

type ActionType = "url" | "phone" | "quick_reply" | "copy_code";

type UrlSource = "meta" | "static";

type CodeSource = "meta" | "static";
type TemplateCategory = "MARKETING" | "UTILITY";

const TEMPLATE_LAYOUT_OPTIONS: { value: LayoutType; label: string; icon: string }[] = [
    { value: "text", label: "Text", icon: TemplateIconText },
    { value: "image_text", label: "Image + Text", icon: TemplateIconImageText },
    { value: "text_buttons", label: "Text + Buttons", icon: TemplateIconTextButton },
    { value: "image_text_buttons", label: "Image + Text + Buttons", icon: TemplateIconImageTextIcon },
    { value: "menu", label: "Menu", icon: TemplateIconMenu },
    { value: "carousel", label: "Carousel", icon: TemplateIconCarousel },
];

const DEFAULT_OPT_OUT_TEXT = "Reply 9 to stop receiving messages from us";
const EMPTY_PREVIEW_TEXT = "Your text will show up here";
const DEFAULT_PREVIEW_IMAGE_URL = DefaultPreviewImage;

function getLayoutLabel(layout: LayoutType): string {
    return TEMPLATE_LAYOUT_OPTIONS.find((option) => option.value === layout)?.label || "Template";
}

function parseJsonMaybe(v: any) {
    if (v == null) return null;
    if (typeof v === "string") {
        try {
            return JSON.parse(v);
        } catch {
            return null;
        }
    }
    return v;
}

function HelpList({ items }: { items: string[] }) {
    return (
        <ul className="v2-template-help-list">
            {items.map((item, idx) => (
                <li key={idx}>
                    <Text type="secondary" className="v2-template-help-list-text">{item}</Text>
                </li>
            ))}
        </ul>
    );
}

function InfoBox({ title, items }: { title: string; items: string[] }) {
    return (
        <Card size="small" style={{ background: "#fafafa" }}>
            <Text strong>{title}</Text>
            <HelpList items={items} />
        </Card>
    );
}

function PlaceholderPicker(props: {
    onInsert: (placeholder: string) => void;
}) {
    const placeholders = [
        "client.first_name",
        "client.last_name",
        "client.full_name",
        "client.email",
        "client.phone",
        "shop.name",
    ];

    return (
        <Card size="small" style={{ background: "#fafafa" }}>
            <Text strong>Supported placeholders</Text>
            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {placeholders.map((p) => (
                    <Tag
                        key={p}
                        color="blue"
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => props.onInsert(`{{${p}}}`)}
                    >
                        {p}
                    </Tag>
                ))}
            </div>
            <HelpList
                items={[
                    "context.* (free-form placeholders are allowed, but not listed here)",
                    "context.images_urls[] and context.buttons[] are reserved for media/actions; not for text body",
                    "Single image/button templates use index 0 from those arrays.",
                ]}
            />
        </Card>
    );
}

function extractVarsFromObject(obj: any): string[] {
    const vars: any = new Set<string>();

    const walk = (v: any) => {
        if (typeof v === "string") {
            const matches = v.match(/{{([^}]+)}}/g);
            if (matches) {
                for (const m of matches) {
                    const token = m.replace(/[{}]/g, "").trim();
                    if (token) vars.add(token);
                }
            }
            return;
        }
        if (Array.isArray(v)) return v.forEach(walk);
        if (v && typeof v === "object") return Object.values(v).forEach(walk);
    };

    walk(obj);
    return [...vars];
}

function collectVarPathsFromConfig(obj: any): string[] {
    const vars: string[] = [];
    const walk = (v: any) => {
        if (!v) return;
        if (Array.isArray(v)) return v.forEach(walk);
        if (typeof v === "object") {
            if (typeof v.url_var_path === "string" && v.url_var_path.trim()) vars.push(v.url_var_path.trim());
            if (typeof v.image_var_path === "string" && v.image_var_path.trim()) vars.push(v.image_var_path.trim());
            if (typeof v.code_var_path === "string" && v.code_var_path.trim()) vars.push(v.code_var_path.trim());
            Object.values(v).forEach(walk);
        }
    };
    walk(obj);
    return vars;
}

function getMetaUrlVarPath(actions: any[], actionIdx: number): string {
    const metaUrlActions = actions.filter(
        (a) => String(a?.type || "").toLowerCase() === "url" && String(a?.url_source || "").toLowerCase() === "meta"
    );
    const action = actions[actionIdx];
    if (!action || String(action?.type || "").toLowerCase() !== "url") return "context.buttons.0.url";
    if (String(action?.url_source || "").toLowerCase() !== "meta") return "context.buttons.0.url";
    if (metaUrlActions.length <= 1) return "context.buttons.0.url";
    let metaIndex = -1;
    for (let i = 0; i <= actionIdx; i++) {
        const a = actions[i];
        if (String(a?.type || "").toLowerCase() === "url" && String(a?.url_source || "").toLowerCase() === "meta") {
            metaIndex += 1;
        }
    }
    return `context.buttons.${Math.max(0, metaIndex)}.url`;
}

function normalizeLanguageKey(input: string | null | undefined): string {
    const raw = String(input || "").trim();
    if (!raw) return "en-US";
    if (LANGUAGES.some((l) => l.key === raw)) return raw;
    const prefix = raw.split("-")[0].toLowerCase();
    const found = LANGUAGES.find((l) => l.key.toLowerCase().startsWith(`${prefix}-`));
    return found?.key || "en-US";
}

function hydratePreviewText(text: string): string {
    const base = String(text || "");
    const known: Record<string, string> = {
        "client.first_name": "Mike",
        "client.last_name": "Levin",
        "client.full_name": "Mike Levin",
        "client.email": "mike@example.com",
        "client.phone": "+14155552671",
        "shop.name": "Vuelve Demo",
    };
    return base.replace(/{{\s*([^}]+)\s*}}/g, (_m, token) => {
        const key = String(token || "").trim();
        return known[key] || `{{${key}}}`;
    });
}

function appendOptOutText(baseText: string, optOutText: string) {
    const body = String(baseText || "").trim();
    const footer = String(optOutText || "").trim();
    if (!footer) return body;
    if (!body) return footer;
    if (body.toLowerCase().endsWith(footer.toLowerCase())) return body;
    return `${body}\n\n${footer}`;
}

function stripOptOutQuickReply(actions: any[]) {
    const list = Array.isArray(actions) ? actions : [];
    return list.filter((a: any) => !(
        String(a?.type || "").toLowerCase() === "quick_reply" &&
        String(a?.id || "").toLowerCase() === "opt_out"
    ));
}

function getInitialPreviewScale() {
    if (typeof window === "undefined") return 1;
    const vh = Math.max(0, window.innerHeight || 0);
    const maxPreviewHeightPx = vh * 0.75;
    const scale = maxPreviewHeightPx / 506;
    return Math.max(0.6, Math.min(scale, 3));
}

type TemplateBuilderPageProps = {
    embedded?: boolean;
    templateIdOverride?: number | null;
    readOnlyOverride?: boolean;
    onSaved?: () => void;
    onTypeLabelChange?: (typeLabel: string) => void;
};

export default function TemplateBuilderPage({
    embedded = false,
    templateIdOverride = null,
    readOnlyOverride,
    onSaved,
    onTypeLabelChange,
}: TemplateBuilderPageProps) {
    const { selectedDomain } = useUserContext();
    const navigate = useNavigate();
    const params = useParams<{ id?: string }>();
    const templateId = templateIdOverride != null ? Number(templateIdOverride) : (Number(params.id || 0) || null);
    const isEditMode = !!templateId;

    // Basic
    const [title, setTitle] = useState("");
    const [layout, setLayout] = useState<LayoutType>("image_text_buttons");
    const [saving, setSaving] = useState(false);
    const [loadingTemplate, setLoadingTemplate] = useState<boolean>(isEditMode);
    const [templateStatus, setTemplateStatus] = useState<string>("draft");
    const [language, setLanguage] = useState<string>("en-US");
    const [templateCategory, setTemplateCategory] = useState<TemplateCategory>("MARKETING");
    const [optOutText, setOptOutText] = useState<string>(DEFAULT_OPT_OUT_TEXT);
    const [previewScale, setPreviewScale] = useState<number>(getInitialPreviewScale);

    // Text
    const [textBody, setTextBody] = useState("");

    // Image + text
    const [imageTextTitle, setImageTextTitle] = useState("");
    const [imageTextBody, setImageTextBody] = useState("");
    const [imageTextSource, setImageTextSource] = useState<ImageSource>("meta");
    const [imageTextUrl, setImageTextUrl] = useState("");
    const [imageTextVarPath] = useState("context.images_urls.0");

    // Text + buttons
    const [ctaBody, setCtaBody] = useState("");
    const [ctaActions, setCtaActions] = useState<any[]>([
        { type: "url", label: "Open", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" },
    ]);

    // Image + text + buttons
    const [cardTitle, setCardTitle] = useState("");
    const [cardBody, setCardBody] = useState("");
    const [cardImageSource, setCardImageSource] = useState<ImageSource>("meta");
    const [cardImageUrl, setCardImageUrl] = useState("");
    const [cardImageVarPath] = useState("context.images_urls.0");
    const [cardActions, setCardActions] = useState<any[]>([
        { type: "url", label: "Open", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" },
    ]);

    // Menu
    const [menuBody, setMenuBody] = useState("");
    const [menuButtonText, setMenuButtonText] = useState("Choose");
    const [menuItems, setMenuItems] = useState<any[]>([
        { id: "item_1", title: "Option 1", description: "" },
        { id: "item_2", title: "Option 2", description: "" },
    ]);

    // Carousel
    const [carouselBody, setCarouselBody] = useState("");
    const [carouselCards, setCarouselCards] = useState<any[]>([
        {
            title_text: "",
            body_text: "Card 1",
            image_source: "meta",
            image_url: "",
            image_var_path: "context.images_urls.0",
            actions: [{ type: "url", label: "Open", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" }],
        },
        {
            title_text: "",
            body_text: "Card 2",
            image_source: "meta",
            image_url: "",
            image_var_path: "context.images_urls.1",
            actions: [{ type: "url", label: "Open", url_source: "meta", url: "", url_var_path: "context.buttons.1.url" }],
        },
    ]);
    const [defaultImageUrl, setDefaultImageUrl] = useState("");
    const [defaultButtonLabel, setDefaultButtonLabel] = useState("");

    const textBodyRef = useRef<any>(null);
    const imageTextBodyRef = useRef<any>(null);
    const ctaBodyRef = useRef<any>(null);
    const cardBodyRef = useRef<any>(null);
    const menuBodyRef = useRef<any>(null);
    const carouselBodyRef = useRef<any>(null);
    const carouselCardBodyRefs = useRef<Array<any>>([]);
    const [activeBodyKey, setActiveBodyKey] = useState<string | null>(null);
    const [selectionMap, setSelectionMap] = useState<Record<string, { start: number; end: number }>>({});

    const getTextAreaEl = (ref: any): HTMLTextAreaElement | null => {
        const el = ref?.current;
        if (!el) return null;
        return el.resizableTextArea?.textArea || el;
    };

    const recordSelection = (key: string, ref: any) => {
        const el = getTextAreaEl(ref);
        if (!el) return;
        setSelectionMap((prev) => ({
            ...prev,
            [key]: { start: el.selectionStart || 0, end: el.selectionEnd || 0 },
        }));
    };

    const insertIntoValue = (
        current: string,
        insert: string,
        sel?: { start: number; end: number }
    ) => {
        const start = sel?.start ?? current.length;
        const end = sel?.end ?? current.length;
        const before = current.slice(0, start);
        const after = current.slice(end);
        const next = `${before}${insert}${after}`;
        const cursor = start + insert.length;
        return { next, cursor };
    };

    const insertPlaceholder = (placeholder: string) => {
        const key = activeBodyKey || (() => {
            if (layout === "text") return "text";
            if (layout === "image_text") return "image_text";
            if (layout === "text_buttons") return "cta";
            if (layout === "image_text_buttons") return "card";
            if (layout === "menu") return "menu";
            if (layout === "carousel") return "carousel";
            return "text";
        })();

        if (key === "text") {
            const { next, cursor } = insertIntoValue(textBody, placeholder, selectionMap[key]);
            setTextBody(next);
            setTimeout(() => {
                const el = getTextAreaEl(textBodyRef);
                if (!el) return;
                el.focus();
                el.setSelectionRange(cursor, cursor);
            }, 0);
            return;
        }
        if (key === "image_text") {
            const { next, cursor } = insertIntoValue(imageTextBody, placeholder, selectionMap[key]);
            setImageTextBody(next);
            setTimeout(() => {
                const el = getTextAreaEl(imageTextBodyRef);
                if (!el) return;
                el.focus();
                el.setSelectionRange(cursor, cursor);
            }, 0);
            return;
        }
        if (key === "cta") {
            const { next, cursor } = insertIntoValue(ctaBody, placeholder, selectionMap[key]);
            setCtaBody(next);
            setTimeout(() => {
                const el = getTextAreaEl(ctaBodyRef);
                if (!el) return;
                el.focus();
                el.setSelectionRange(cursor, cursor);
            }, 0);
            return;
        }
        if (key === "card") {
            const { next, cursor } = insertIntoValue(cardBody, placeholder, selectionMap[key]);
            setCardBody(next);
            setTimeout(() => {
                const el = getTextAreaEl(cardBodyRef);
                if (!el) return;
                el.focus();
                el.setSelectionRange(cursor, cursor);
            }, 0);
            return;
        }
        if (key === "menu") {
            const { next, cursor } = insertIntoValue(menuBody, placeholder, selectionMap[key]);
            setMenuBody(next);
            setTimeout(() => {
                const el = getTextAreaEl(menuBodyRef);
                if (!el) return;
                el.focus();
                el.setSelectionRange(cursor, cursor);
            }, 0);
            return;
        }
        if (key.startsWith("carousel_card_")) {
            const idx = Number(key.replace("carousel_card_", ""));
            if (!Number.isFinite(idx)) return;
            const card = carouselCards[idx];
            if (!card) return;
            const { next, cursor } = insertIntoValue(card.body_text || "", placeholder, selectionMap[key]);
            const nextCards = [...carouselCards];
            nextCards[idx] = { ...card, body_text: next };
            setCarouselCards(nextCards);
            setTimeout(() => {
                const ref = carouselCardBodyRefs.current[idx];
                const el = getTextAreaEl(ref);
                if (!el) return;
                el.focus();
                el.setSelectionRange(cursor, cursor);
            }, 0);
            return;
        }
        if (key === "carousel") {
            const { next, cursor } = insertIntoValue(carouselBody, placeholder, selectionMap[key]);
            setCarouselBody(next);
            setTimeout(() => {
                const el = getTextAreaEl(carouselBodyRef);
                if (!el) return;
                el.focus();
                el.setSelectionRange(cursor, cursor);
            }, 0);
        }
    };

    const setUnifiedBody = (value: string) => {
        setTextBody(value);
        setImageTextBody(value);
        setCtaBody(value);
        setCardBody(value);
        setMenuBody(value);
        setCarouselBody(value);
    };

    const setUnifiedTitle = (value: string) => {
        setImageTextTitle(value);
        setCardTitle(value);
    };

    useEffect(() => {
        if (!selectedDomain || !isEditMode || !templateId) return;
        setLoadingTemplate(true);
        GetV2Template(selectedDomain, templateId)
            .then((res) => {
                if (!res.ok || !res.data?.template) {
                    message.error(res.error || "Failed to load template");
                    return;
                }
                const t: any = res.data.template;
                const types = Array.isArray(parseJsonMaybe(t.content_types_json)) ? parseJsonMaybe(t.content_types_json) : [];
                const cfg = parseJsonMaybe(t.types_config_json) || {};
                const metadata = parseJsonMaybe(t.metadata_json) || {};

                setTitle(String(t.title || ""));
                setTemplateStatus(String(t.status || "draft").toLowerCase());
                setLanguage(normalizeLanguageKey(String(t.language || "")));
                setTemplateCategory(String(t.category || "MARKETING").toUpperCase() === "UTILITY" ? "UTILITY" : "MARKETING");
                if (metadata?.defaults?.default_image_url) setDefaultImageUrl(String(metadata.defaults.default_image_url));
                if (metadata?.defaults?.default_button_label) setDefaultButtonLabel(String(metadata.defaults.default_button_label));
                if (metadata?.defaults?.opt_out_text) setOptOutText(String(metadata.defaults.opt_out_text));

                if (types.includes("twilio/list-picker")) {
                    setLayout("menu");
                    const c = cfg["twilio/list-picker"] || {};
                    const nextBody = String(c.body_text || "");
                    setUnifiedBody(nextBody);
                    setMenuButtonText(String(c.button_text || "Choose"));
                    setMenuItems(Array.isArray(c.items) && c.items.length ? c.items : [
                        { id: "item_1", title: "Option 1", description: "" },
                        { id: "item_2", title: "Option 2", description: "" },
                    ]);
                    return;
                }

                if (types.includes("twilio/carousel")) {
                    setLayout("carousel");
                    const c = cfg["twilio/carousel"] || {};
                    const nextBody = String(c.body_text || "");
                    setUnifiedBody(nextBody);
                    const cards = Array.isArray(c.cards) ? c.cards : [];
                    if (cards.length) {
                        setCarouselCards(cards.map((card: any) => ({
                            ...card,
                            actions: stripOptOutQuickReply(card?.actions),
                        })));
                    }
                    return;
                }

                if (types.includes("twilio/call-to-action")) {
                    setLayout("text_buttons");
                    const c = cfg["twilio/call-to-action"] || {};
                    const nextBody = String(c.body_text || c.body || "");
                    setUnifiedBody(nextBody);
                    const actions = Array.isArray(c.actions) ? c.actions : [];
                    if (actions.length) setCtaActions(stripOptOutQuickReply(actions));
                    return;
                }

                if (types.includes("twilio/card")) {
                    const c = cfg["twilio/card"] || {};
                    const kind = String(c.kind || "");
                    const hasActions = Array.isArray(c.actions) && c.actions.length > 0;
                    const hasImage = String(c.image_source || "").toLowerCase() === "meta" || !!String(c.image_url || "").trim() || (Array.isArray(c.media) && c.media.length > 0);
                    if (kind === "image_text_buttons" || (hasImage && hasActions)) {
                        setLayout("image_text_buttons");
                        setUnifiedTitle(String(c.title_text || c.title || ""));
                        const nextBody = String(c.body_text || c.body || "");
                        setUnifiedBody(nextBody);
                        setCardImageSource(String(c.image_source || "").toLowerCase() === "static" ? "static" : "meta");
                        setCardImageUrl(String(c.image_url || ""));
                        if (hasActions) setCardActions(stripOptOutQuickReply(c.actions));
                    } else {
                        setLayout("image_text");
                        setUnifiedTitle(String(c.title_text || c.title || ""));
                        const nextBody = String(c.body_text || c.body || "");
                        setUnifiedBody(nextBody);
                        setImageTextSource(String(c.image_source || "").toLowerCase() === "static" ? "static" : "meta");
                        setImageTextUrl(String(c.image_url || ""));
                    }
                    return;
                }

                setLayout("text");
                const tx = cfg["twilio/text"] || {};
                setUnifiedBody(String(tx.body || ""));
            })
            .finally(() => setLoadingTemplate(false));
    }, [selectedDomain, isEditMode, templateId]);

    useEffect(() => {
        if (!isEditMode) setLoadingTemplate(false);
    }, [isEditMode, templateId]);

    useEffect(() => {
        if (!onTypeLabelChange) return;
        onTypeLabelChange(getLayoutLabel(layout));
    }, [layout, onTypeLabelChange]);

    const { content_types, types_config, metadata } = useMemo(() => {
        const defaults: any = {};
        if (defaultImageUrl.trim()) defaults.default_image_url = defaultImageUrl.trim();
        if (defaultButtonLabel.trim()) defaults.default_button_label = defaultButtonLabel.trim();
        if (layout !== "menu" && templateCategory === "MARKETING" && optOutText.trim()) defaults.opt_out_text = optOutText.trim();
        const metadataBase = Object.keys(defaults).length ? { defaults } : {};

        if (layout === "text") {
            const cfg = {
                body: textBody || "",
            };
            return {
                content_types: ["twilio/text"],
                types_config: { "twilio/text": cfg },
                metadata: metadataBase,
            };
        }

        if (layout === "image_text") {
            const cardCfg: any = {
                kind: "image_text_buttons",
                title_text: imageTextTitle || "",
                body_text: imageTextBody || "",
                image_source: imageTextSource,
                image_url: imageTextSource === "static" ? imageTextUrl || "" : null,
                image_var_path: imageTextSource === "meta" ? imageTextVarPath || "context.images_urls.0" : null,
                actions: [],
            };
            return {
                content_types: ["twilio/card"],
                types_config: { "twilio/card": cardCfg },
                metadata: metadataBase,
            };
        }

        if (layout === "text_buttons") {
            const normalizedActions = stripOptOutQuickReply(ctaActions);
            const cfg: any = {
                kind: "text_buttons",
                body_text: ctaBody || "",
                actions: normalizedActions.map((a: any, idx: number) => {
                    if (String(a?.type || "").toLowerCase() !== "url") return a;
                    if (String(a?.url_source || "").toLowerCase() !== "meta") return a;
                    const varPath = getMetaUrlVarPath(normalizedActions, idx);
                    return { ...a, url_var_path: varPath };
                }),
            };
            return {
                content_types: ["twilio/call-to-action"],
                types_config: { "twilio/call-to-action": cfg },
                metadata: metadataBase,
            };
        }

        if (layout === "image_text_buttons") {
            const normalizedActions = stripOptOutQuickReply(cardActions);
            const cfg: any = {
                kind: "image_text_buttons",
                title_text: cardTitle || "",
                body_text: cardBody || "",
                image_source: cardImageSource,
                image_url: cardImageSource === "static" ? cardImageUrl || "" : null,
                image_var_path: cardImageSource === "meta" ? cardImageVarPath || "context.images_urls.0" : null,
                actions: normalizedActions.map((a: any, idx: number) => {
                    if (String(a?.type || "").toLowerCase() !== "url") return a;
                    if (String(a?.url_source || "").toLowerCase() !== "meta") return a;
                    const varPath = getMetaUrlVarPath(normalizedActions, idx);
                    return { ...a, url_var_path: varPath };
                }),
            };
            return {
                content_types: ["twilio/card"],
                types_config: { "twilio/card": cfg },
                metadata: metadataBase,
            };
        }

        if (layout === "menu") {
            const cfg: any = {
                kind: "menu",
                body_text: menuBody || "",
                button_text: menuButtonText || "",
                items: menuItems,
            };
            return {
                content_types: ["twilio/list-picker"],
                types_config: { "twilio/list-picker": cfg },
                metadata: metadataBase,
            };
        }

        const cfg: any = {
            kind: "carousel",
            body_text: carouselBody || "",
            cards: carouselCards.map((c: any, cardIndex: number) => {
                const cardActions = stripOptOutQuickReply(c.actions);
                const mappedActions = cardActions.map((a: any, idx: number) => {
                    if (String(a?.type || "").toLowerCase() !== "url") return a;
                    if (String(a?.url_source || "").toLowerCase() !== "meta") return a;
                    const varPath = getMetaUrlVarPath(cardActions, idx);
                    return { ...a, url_var_path: varPath };
                });
                const imageVarPath = String(c?.image_source || "").toLowerCase() === "meta"
                    ? `context.images_urls.${cardIndex}`
                    : c?.image_var_path || null;
                return { ...c, image_var_path: imageVarPath, actions: mappedActions };
            }),
        };
        return {
            content_types: ["twilio/carousel"],
            types_config: { "twilio/carousel": cfg },
            metadata: metadataBase,
        };
    }, [
        layout,
        templateCategory,
        defaultImageUrl,
        defaultButtonLabel,
        optOutText,
        textBody,
        imageTextTitle,
        imageTextBody,
        imageTextSource,
        imageTextUrl,
        imageTextVarPath,
        ctaBody,
        ctaActions,
        cardTitle,
        cardBody,
        cardImageSource,
        cardImageUrl,
        cardImageVarPath,
        cardActions,
        menuBody,
        menuButtonText,
        menuItems,
        carouselBody,
        carouselCards,
    ]);

    const previewText = useMemo(() => {
        const sharedTitle = String(cardTitle || imageTextTitle || "").trim();
        const withOptOut = (text: string) => (layout !== "menu" && templateCategory === "MARKETING") ? appendOptOutText(text, optOutText) : text;
        if (layout === "text") return withOptOut(hydratePreviewText(textBody || ""));
        if (layout === "image_text") return withOptOut(hydratePreviewText([imageTextTitle, imageTextBody].filter(Boolean).join("\n").trim()));
        if (layout === "text_buttons") return withOptOut(hydratePreviewText(ctaBody || ""));
        if (layout === "image_text_buttons") return withOptOut(hydratePreviewText([cardTitle, cardBody].filter(Boolean).join("\n").trim()));
        if (layout === "menu") {
            const firstTwo = (menuItems || []).slice(0, 2).map((i: any) => `• ${String(i?.title || "").trim()}`).filter(Boolean);
            return withOptOut(hydratePreviewText([sharedTitle, menuBody, ...firstTwo].filter(Boolean).join("\n").trim()));
        }
        if (layout === "carousel") {
            const first = Array.isArray(carouselCards) && carouselCards.length ? carouselCards[0] : null;
            return withOptOut(hydratePreviewText([sharedTitle, carouselBody, first?.title_text || "", first?.body_text || ""].filter(Boolean).join("\n").trim()));
        }
        return "";
    }, [layout, textBody, imageTextBody, imageTextTitle, ctaBody, cardBody, cardTitle, menuBody, menuItems, carouselBody, carouselCards, optOutText, templateCategory]);

    const hasPrimaryPreviewText = useMemo(() => {
        if (layout === "text") return !!String(textBody || "").trim();
        if (layout === "image_text") return !!String(imageTextTitle || imageTextBody || "").trim();
        if (layout === "text_buttons") return !!String(ctaBody || "").trim();
        if (layout === "image_text_buttons") return !!String(cardTitle || cardBody || "").trim();
        if (layout === "menu") {
            const hasItems = Array.isArray(menuItems) && menuItems.some((i: any) =>
                String(i?.title || "").trim() || String(i?.description || "").trim()
            );
            return !!String(menuBody || "").trim() || hasItems;
        }
        if (layout === "carousel") {
            const hasCardText = Array.isArray(carouselCards) && carouselCards.some((card: any) =>
                String(card?.title_text || "").trim() || String(card?.body_text || "").trim()
            );
            return !!String(carouselBody || "").trim() || hasCardText;
        }
        return false;
    }, [
        layout,
        textBody,
        imageTextTitle,
        imageTextBody,
        ctaBody,
        cardTitle,
        cardBody,
        menuBody,
        menuItems,
        carouselBody,
        carouselCards,
    ]);

    const previewDisplayText = useMemo(() => {
        if (hasPrimaryPreviewText) return previewText;
        if (layout !== "menu" && templateCategory === "MARKETING") {
            return appendOptOutText(EMPTY_PREVIEW_TEXT, optOutText);
        }
        return EMPTY_PREVIEW_TEXT;
    }, [hasPrimaryPreviewText, previewText, layout, templateCategory, optOutText]);

    const previewImageUrl = useMemo(() => {
        if (layout === "image_text") {
            if (imageTextSource === "static" && String(imageTextUrl || "").trim()) return String(imageTextUrl).trim();
            if (String(defaultImageUrl || "").trim()) return String(defaultImageUrl).trim();
            return DEFAULT_PREVIEW_IMAGE_URL;
        }
        if (layout === "image_text_buttons") {
            if (cardImageSource === "static" && String(cardImageUrl || "").trim()) return String(cardImageUrl).trim();
            if (String(defaultImageUrl || "").trim()) return String(defaultImageUrl).trim();
            return DEFAULT_PREVIEW_IMAGE_URL;
        }
        if (layout === "carousel") {
            const first = Array.isArray(carouselCards) && carouselCards.length ? carouselCards[0] : null;
            const firstUrl = String(first?.image_url || "").trim();
            if (String(first?.image_source || "").toLowerCase() === "static" && firstUrl) return firstUrl;
            if (String(defaultImageUrl || "").trim()) return String(defaultImageUrl).trim();
            return DEFAULT_PREVIEW_IMAGE_URL;
        }
        return undefined;
    }, [
        layout,
        imageTextSource,
        imageTextUrl,
        cardImageSource,
        cardImageUrl,
        carouselCards,
        defaultImageUrl,
    ]);

    const direction: "ltr" | "rtl" = useMemo(() => {
        const selected = LANGUAGES.find((l) => l.key === language);
        return selected?.direction === "rtl" ? "rtl" : "ltr";
    }, [language]);

    const previewButtons = useMemo(() => {
        if (layout === "text" || layout === "image_text") return [];
        if (layout === "text_buttons") {
            const labels = stripOptOutQuickReply(ctaActions)
                .map((a: any) => String(a?.label || "").trim())
                .filter(Boolean);
            return labels.slice(0, 2);
        }
        if (layout === "image_text_buttons") {
            const labels = stripOptOutQuickReply(cardActions)
                .map((a: any) => String(a?.label || "").trim())
                .filter(Boolean);
            return labels.slice(0, 2);
        }
        return [] as string[];
    }, [layout, ctaActions, cardActions]);

    const previewCarouselCards = useMemo(() => {
        if (layout !== "carousel") return [];
        return (Array.isArray(carouselCards) ? carouselCards : []).slice(0, 3).map((card: any) => {
            const source = String(card?.image_source || "").toLowerCase();
            const image = source === "static"
                ? String(card?.image_url || "").trim()
                : String(defaultImageUrl || "").trim();
            const labels = stripOptOutQuickReply(card?.actions)
                .map((a: any) => String(a?.label || "").trim())
                .filter(Boolean)
                .slice(0, 2);
            const rawBody = hydratePreviewText(String(card?.body_text || ""));
            const bodyWithPlaceholder = rawBody || EMPTY_PREVIEW_TEXT;
            return {
                title: hydratePreviewText(String(card?.title_text || "")),
                body: bodyWithPlaceholder,
                image: image || DEFAULT_PREVIEW_IMAGE_URL,
                buttons: labels,
            };
        });
    }, [layout, carouselCards, defaultImageUrl]);

    useEffect(() => {
        const recalc = () => {
            const vh = Math.max(0, window.innerHeight || 0);
            const maxPreviewHeightPx = vh * 0.75;
            const scale = maxPreviewHeightPx / 506;
            setPreviewScale(Math.max(0.6, Math.min(scale, 3)));
        };
        recalc();
        window.addEventListener("resize", recalc);
        return () => window.removeEventListener("resize", recalc);
    }, []);

    useEffect(() => {
        if (isEditMode) return;
        setTitle("");
        setTemplateStatus("draft");
        setLanguage("en-US");
        setTemplateCategory("MARKETING");
        setLayout("image_text_buttons");
        setUnifiedBody("");
        setUnifiedTitle("");
        setImageTextSource("meta");
        setImageTextUrl("");
        setCtaActions([{ type: "url", label: "Open", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" }]);
        setCardImageSource("meta");
        setCardImageUrl("");
        setCardActions([{ type: "url", label: "Open", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" }]);
        setMenuButtonText("Choose");
        setMenuItems([
            { id: "item_1", title: "Option 1", description: "" },
            { id: "item_2", title: "Option 2", description: "" },
        ]);
        setCarouselCards([
            {
                title_text: "",
                body_text: "Card 1",
                image_source: "meta",
                image_url: "",
                image_var_path: "context.images_urls.0",
                actions: [{ type: "url", label: "Open", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" }],
            },
            {
                title_text: "",
                body_text: "Card 2",
                image_source: "meta",
                image_url: "",
                image_var_path: "context.images_urls.1",
                actions: [{ type: "url", label: "Open", url_source: "meta", url: "", url_var_path: "context.buttons.1.url" }],
            },
        ]);
        setDefaultImageUrl("");
        setDefaultButtonLabel("");
        setOptOutText(DEFAULT_OPT_OUT_TEXT);
    }, [isEditMode, templateId]);

    const isReadOnly = typeof readOnlyOverride === "boolean"
        ? readOnlyOverride
        : (isEditMode && templateStatus !== "draft");
    const showPreview = !isEditMode || !loadingTemplate;
    const isCreateStepFlow = !isEditMode;
    const isDraftEditTabbed = isEditMode && !isReadOnly;
    const useStepSections = isCreateStepFlow || isDraftEditTabbed;
    const isMenuLayout = layout === "menu";
    const isCarouselLayout = layout === "carousel";
    const includesImageStep = ["image_text", "image_text_buttons", "carousel"].includes(layout);
    const includesButtonsStep = ["text_buttons", "image_text_buttons", "carousel"].includes(layout);
    const totalWizardSteps = isCarouselLayout ? 5 : isMenuLayout ? 3 : 2 + (includesImageStep ? 1 : 0) + (includesButtonsStep ? 1 : 0);
    const [createStep, setCreateStep] = useState<number>(1);
    const menuItemsStepIndex = isMenuLayout ? 3 : null;
    const cardsStepIndex = isCarouselLayout ? 3 : null;
    const imageStepIndex = includesImageStep ? (isCarouselLayout ? 4 : 3) : null;
    const buttonsStepIndex = includesButtonsStep ? (isCarouselLayout ? 5 : (includesImageStep ? 4 : 3)) : null;

    const stepTabs = useMemo(() => {
        const items: Array<{ key: string; label: string }> = [
            { key: "1", label: "Setup" },
            { key: "2", label: "Content" },
        ];
        if (isMenuLayout && menuItemsStepIndex) items.push({ key: String(menuItemsStepIndex), label: "Items" });
        if (isCarouselLayout && cardsStepIndex) items.push({ key: String(cardsStepIndex), label: "Cards" });
        if (includesImageStep && imageStepIndex) items.push({ key: String(imageStepIndex), label: "Image" });
        if (includesButtonsStep && buttonsStepIndex) items.push({ key: String(buttonsStepIndex), label: "Buttons" });
        return items;
    }, [isMenuLayout, menuItemsStepIndex, isCarouselLayout, cardsStepIndex, includesImageStep, includesButtonsStep, imageStepIndex, buttonsStepIndex]);

    useEffect(() => {
        if (!useStepSections) return;
        setCreateStep((prev) => Math.min(prev, totalWizardSteps));
    }, [useStepSections, totalWizardSteps]);

    const canGoNext = useMemo(() => {
        if (!isCreateStepFlow) return false;
        if (createStep === 1) return !!title.trim();
        return true;
    }, [isCreateStepFlow, createStep, title]);

    const validationError = useMemo(() => {
        if (!selectedDomain) return "Missing selected domain";
        if (!title.trim()) return "Template title is required";
        if (layout !== "menu" && templateCategory === "MARKETING" && !optOutText.trim()) {
            return "Opt-out footer text is required for marketing templates";
        }

        if (layout === "text") {
            if (!textBody.trim()) return "Text body is required";
            return null;
        }

        if (layout === "image_text") {
            if (!imageTextTitle.trim() && !imageTextBody.trim()) return "Title or body is required";
            if (imageTextSource === "static" && !imageTextUrl.trim()) return "Static image URL is required";
            return null;
        }

        if (layout === "text_buttons") {
            if (!ctaBody.trim()) return "Body text is required";
            if (!ctaActions.length) return "At least one action is required";
            for (const a of ctaActions) {
                const t = String(a?.type || "").toLowerCase();
                if (!String(a?.label || "").trim()) return "Action label is required";
                if (t === "url" && String(a?.url_source || "").toLowerCase() === "static" && !String(a?.url || "").trim()) {
                    return "Static URL is required";
                }
                if (t === "phone" && !String(a?.phone || "").trim()) return "Phone is required";
                if (t === "copy_code") {
                    const src = String(a?.code_source || "").toLowerCase();
                    if (src === "static" && !String(a?.code || "").trim()) return "Code is required";
                    if (src === "meta" && !String(a?.code_var_path || "").trim()) return "Code var path is required";
                }
            }
            return null;
        }

        if (layout === "image_text_buttons") {
            if (!cardTitle.trim() && !cardBody.trim()) return "Title or body is required";
            if (cardImageSource === "static" && !cardImageUrl.trim()) return "Static image URL is required";
            if (!cardActions.length) return "At least one action is required";
            for (const a of cardActions) {
                const t = String(a?.type || "").toLowerCase();
                if (!String(a?.label || "").trim()) return "Action label is required";
                if (t === "url" && String(a?.url_source || "").toLowerCase() === "static" && !String(a?.url || "").trim()) {
                    return "Static URL is required";
                }
                if (t === "phone" && !String(a?.phone || "").trim()) return "Phone is required";
                if (t === "quick_reply" && !String(a?.id || "").trim()) return "Quick reply id is required";
            }
            return null;
        }

        if (layout === "menu") {
            if (!menuBody.trim()) return "Menu body is required";
            if (!menuButtonText.trim()) return "Menu button text is required";
            if (menuItems.length < 2) return "Menu must have at least 2 items";
            for (const item of menuItems) {
                if (!String(item?.title || "").trim()) return "Each item needs a title";
                if (!String(item?.id || "").trim()) return "Each item needs an id";
            }
            return null;
        }

        if (layout === "carousel") {
            if (carouselCards.length < 2) return "Carousel must have at least 2 cards";
            for (const c of carouselCards) {
                if (!String(c?.body_text || "").trim()) return "Each card needs body text";
                if (String(c?.image_source || "").toLowerCase() === "static" && !String(c?.image_url || "").trim()) {
                    return "Static image URL is required for carousel cards";
                }
                for (const a of c?.actions || []) {
                    const t = String(a?.type || "").toLowerCase();
                    if (!String(a?.label || "").trim()) return "Action label is required";
                    if (t === "url" && String(a?.url_source || "").toLowerCase() === "static" && !String(a?.url || "").trim()) {
                        return "Static URL is required";
                    }
                    if (t === "phone" && !String(a?.phone || "").trim()) return "Phone is required";
                    if (t === "quick_reply" && !String(a?.id || "").trim()) return "Quick reply id is required";
                }
            }
        }

        return null;
    }, [
        selectedDomain,
        title,
        layout,
        templateCategory,
        optOutText,
        textBody,
        imageTextTitle,
        imageTextBody,
        imageTextSource,
        imageTextUrl,
        ctaBody,
        ctaActions,
        cardTitle,
        cardBody,
        cardImageSource,
        cardImageUrl,
        cardActions,
        menuBody,
        menuButtonText,
        menuItems,
        carouselCards,
    ]);

    const variables_schema = useMemo(() => {
        const base: any = new Set<string>();

        const extracted = extractVarsFromObject(types_config);
        extracted.forEach((v) => base.add(v));

        const varPaths = collectVarPathsFromConfig(types_config);
        varPaths.forEach((v) => base.add(v));

        if (layout === "text_buttons") {
            ctaActions.forEach((a, idx) => {
                const t = String(a?.type || "").toLowerCase();
                if (t === "url" && String(a?.url_source || "").toLowerCase() === "meta") {
                    base.add(getMetaUrlVarPath(ctaActions, idx));
                }
                if (t === "copy_code" && String(a?.code_source || "").toLowerCase() === "meta") {
                    const p = String(a?.code_var_path || "").trim();
                    if (p) base.add(p);
                }
            });
        }

        if (layout === "image_text" && imageTextSource === "meta") {
            base.add(String(imageTextVarPath || "context.images_urls.0").trim() || "context.images_urls.0");
        }

        if (layout === "image_text_buttons" && cardImageSource === "meta") {
            base.add(String(cardImageVarPath || "context.images_urls.0").trim() || "context.images_urls.0");
        }

        if (layout === "image_text_buttons") {
            cardActions.forEach((a, idx) => {
                if (String(a?.type || "").toLowerCase() === "url" && String(a?.url_source || "").toLowerCase() === "meta") {
                    base.add(getMetaUrlVarPath(cardActions, idx));
                }
            });
        }

        if (layout === "carousel") {
            carouselCards.forEach((c, idx) => {
                if (String(c?.image_source || "").toLowerCase() === "meta") {
                    base.add(`context.images_urls.${idx}`);
                }
                (c?.actions || []).forEach((a: any, aIdx: number) => {
                    if (String(a?.type || "").toLowerCase() === "url" && String(a?.url_source || "").toLowerCase() === "meta") {
                        base.add(getMetaUrlVarPath(c?.actions || [], aIdx));
                    }
                });
            });
        }

        return [...base];
    }, [
        types_config,
        layout,
        ctaActions,
        imageTextSource,
        imageTextVarPath,
        cardImageSource,
        cardImageVarPath,
        cardActions,
        carouselCards,
    ]);

    const save = async () => {
        if (!selectedDomain) return;
        if (isReadOnly) return message.error("Submitted templates cannot be edited");
        if (validationError) return message.error(validationError);

        setSaving(true);
        try {
            const body = {
                channel: "whatsapp" as const,
                provider: "twilio" as const,
                title: title.trim(),
                language,
                category: layout === "menu" ? "UTILITY" : templateCategory,
                content_types,
                types_config,
                variables_schema,
                metadata,
            };
            const res = isEditMode && templateId
                ? await UpdateV2Template(selectedDomain, templateId, body)
                : await CreateV2Template(selectedDomain, body);

            if (!res.ok) {
                message.error(res.error || (isEditMode ? "Failed to update template" : "Failed to create template"));
                return;
            }

            message.success(isEditMode ? `Template updated (#${templateId})` : `Template created (#${res.data?.id})`);
            if (onSaved) {
                onSaved();
            } else {
                navigate("/templates/v2");
            }
        } finally {
            setSaving(false);
        }
    };

    if (!selectedDomain) return null;

    const content = (
        <Card className="v2-template-builder-card">
                {loadingTemplate ? (
                    <div className="v2-template-builder-loading-block">
                        <Loader size={124} isFullPage={false} />
                    </div>
                ) : null}
                {!loadingTemplate ? (
                <fieldset
                    disabled={loadingTemplate || isReadOnly}
                    style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
                >
                    <ConfigProvider getPopupContainer={(node) => node?.closest(".panel-content") as HTMLElement || document.body}>
                        <div className="v2-template-builder-preview-slot">
                            <div
                                className={`v2-template-builder-preview-wrapper ${showPreview ? "ready" : "loading"}`}
                                style={{ ["--preview-scale" as any]: previewScale }}
                            >
                                <div className="v2-template-builder-preview-scale">
                                    {showPreview ? (
                                        <div className="v2-template-preview-shell">
                                            <img src={WhatsappScreen} className="v2-template-preview-phone-frame" />
                                            <div
                                                className="v2-template-preview-chat"
                                                style={{ backgroundImage: `url(${WhatsappWallpaper})` }}
                                            >
                                                <div className="v2-template-preview-chat-scroll">
                                                    <div className="v2-template-preview-message-row">
                                                        <div className="v2-template-preview-sender">
                                                            <img src={StoreWhatsappImage} />
                                                        </div>
                                                        <div className="v2-template-preview-bubble" style={{ direction }}>
                                                            {layout === "carousel" ? (
                                                                <>
                                                                    {carouselBody ? <div className="v2-template-preview-text">{hydratePreviewText(carouselBody)}</div> : null}
                                                                    <div className="v2-template-preview-carousel">
                                                                        {previewCarouselCards.map((card: any, idx: number) => (
                                                                            <div key={idx} className="v2-template-preview-carousel-card">
                                                                                {card.image ? (
                                                                                    <img className="v2-template-preview-image" src={card.image} />
                                                                                ) : null}
                                                                                {card.title ? <div className="v2-template-preview-card-title">{card.title}</div> : null}
                                                                                {card.body ? <div className="v2-template-preview-card-body">{card.body}</div> : null}
                                                                                {card.buttons?.length ? (
                                                                                    <div className="v2-template-preview-actions">
                                                                                        {card.buttons.map((btn: string, buttonIndex: number) => (
                                                                                            <button key={`${idx}-${buttonIndex}`} type="button">{btn}</button>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : null}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {previewImageUrl ? (
                                                                        <img
                                                                            className="v2-template-preview-image"
                                                                            src={previewImageUrl}
                                                                            onError={(e) => {
                                                                                const img = e.currentTarget as HTMLImageElement;
                                                                                if (img.src !== DEFAULT_PREVIEW_IMAGE_URL) {
                                                                                    img.src = DEFAULT_PREVIEW_IMAGE_URL;
                                                                                }
                                                                            }}
                                                                        />
                                                                    ) : null}
                                                                    <div className={`v2-template-preview-text ${!hasPrimaryPreviewText ? "v2-template-preview-placeholder" : ""}`}>
                                                                        {previewDisplayText}
                                                                    </div>
                                                                    {layout === "menu" ? (
                                                                        <div className="v2-template-preview-menu">
                                                                            <div className="v2-template-preview-menu-opener">
                                                                                <button type="button">{menuButtonText || "Choose"}</button>
                                                                            </div>
                                                                            <div className="v2-template-preview-menu-items">
                                                                                {(menuItems || []).map((item: any, idx: number) => (
                                                                                    <div key={idx} className="v2-template-preview-menu-item">
                                                                                        <strong>{String(item?.title || "").trim() || `Option ${idx + 1}`}</strong>
                                                                                        {String(item?.description || "").trim() ? (
                                                                                            <span>{String(item.description).trim()}</span>
                                                                                        ) : null}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    ) : previewButtons.length ? (
                                                                        <div className="v2-template-preview-actions">
                                                                            {previewButtons.map((label: string, idx: number) => (
                                                                                <button key={idx} type="button">{label}</button>
                                                                            ))}
                                                                        </div>
                                                                    ) : null}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <Space direction="vertical" style={{ width: "100%" }} size="middle" className="v2-template-builder-main-space">
                {isDraftEditTabbed ? (
                    <Tabs
                        className="v2-template-builder-tabs"
                        activeKey={String(createStep)}
                        onChange={(key) => setCreateStep(Number(key))}
                        items={stepTabs.map((tab) => ({ key: tab.key, label: tab.label }))}
                    />
                ) : null}
                {!useStepSections || createStep === 1 ? (
                    <>
                        <div className="v2-template-builder-section-box">
                            <div className="v2-template-builder-header-row">
                                <Input
                                    className="v2-template-builder-title-input"
                                    placeholder="Template title"
                                    value={title}
                                    dir={direction}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                                <div className="v2-template-builder-language-wrap">
                                    <LanguagePicker selectedKey={language} onClick={setLanguage} label="Language" />
                                </div>
                            </div>
                            <Space direction="vertical" style={{ width: "100%" }} size={8}>
                                <Text strong className="v2-template-builder-section-title">Template Type</Text>
                                <div className="v2-template-layout-buttons">
                                    {TEMPLATE_LAYOUT_OPTIONS.map((option) => (
                                        <Tooltip key={option.value} title={option.label}>
                                            <Button size="large"
                                                type="default"
                                                className={`v2-template-layout-btn ${layout === option.value ? "active" : ""}`}
                                                onClick={() => setLayout(option.value)}
                                            >
                                                <img src={option.icon} alt={option.label} />
                                            </Button>
                                        </Tooltip>
                                    ))}
                                </div>
                            </Space>
                        </div>
                        {layout !== "menu" ? (
                            <div className="v2-template-builder-section-box">
                                <Space direction="vertical" style={{ width: "100%" }} size={6}>
                                    <Text strong className="v2-template-builder-section-title">Message category</Text>
                                    <Text type="secondary">
                                        Choose <strong>Utility</strong> for service updates the customer expects (order, payment, shipping, account).{" "}
                                        Choose <strong>Marketing</strong> for promotions, reminders, offers, or re-engagement campaigns.
                                    </Text>
                                    <Select
                                        className="v2-template-category-select"
                                        value={templateCategory}
                                        onChange={(value) => setTemplateCategory(value as TemplateCategory)}
                                        options={[
                                            { value: "MARKETING", label: "Marketing" },
                                            { value: "UTILITY", label: "Utility" },
                                        ]}
                                    />
                                </Space>
                            </div>
                        ) : null}
                    </>
                ) : null}
                {(!useStepSections || createStep === 2) && layout === "text" ? (
                    <>
                        <div className="v2-template-builder-section-box">
                            <Input.TextArea
                                rows={5}
                                placeholder="Body text (use {{context.*}} placeholders)"
                                value={textBody}
                                dir={direction}
                                onChange={(e) => setUnifiedBody(e.target.value)}
                                ref={textBodyRef}
                                onFocus={() => setActiveBodyKey("text")}
                                onSelect={() => recordSelection("text", textBodyRef)}
                                onKeyUp={() => recordSelection("text", textBodyRef)}
                            />
                            <PlaceholderPicker onInsert={insertPlaceholder} />
                            <Text type="secondary">
                                Example: <code>{"Only 3 nights left at {{context.room_name}}"}</code>
                            </Text>
                            <HelpList
                                items={[
                                    "Outside the 24-hour customer care window, WhatsApp requires an approved template.",
                                    "If you use variables, avoid starting/ending the body with a variable or placing variables back-to-back.",
                                ]}
                            />
                        </div>
                    </>
                ) : null}

                {layout === "image_text" ? (
                    <>
                        {!useStepSections || createStep === 2 ? (
                            <>
                                <div className="v2-template-builder-section-box">
                                    <Input
                                        placeholder="Title (optional)"
                                        value={imageTextTitle}
                                        dir={direction}
                                        onChange={(e) => setUnifiedTitle(e.target.value)}
                                    />
                                </div>
                                <div className="v2-template-builder-section-box">
                                    <Input.TextArea
                                        rows={4}
                                        placeholder="Body (optional, supports {{context.*}})"
                                        value={imageTextBody}
                                        dir={direction}
                                        onChange={(e) => setUnifiedBody(e.target.value)}
                                        ref={imageTextBodyRef}
                                        onFocus={() => setActiveBodyKey("image_text")}
                                        onSelect={() => recordSelection("image_text", imageTextBodyRef)}
                                        onKeyUp={() => recordSelection("image_text", imageTextBodyRef)}
                                    />
                                    <PlaceholderPicker onInsert={insertPlaceholder} />
                                </div>
                            </>
                        ) : null}
                        {!useStepSections || createStep === 3 ? (
                            <>
                                {!useStepSections ? <Divider /> : null}
                                <div className="v2-template-builder-section-box">
                                    <Space direction="vertical" style={{ width: "100%" }}>
                                        <Text className="v2-template-builder-section-title">Image Source</Text>
                                        <Select
                                            value={imageTextSource}
                                            onChange={(v) => setImageTextSource(v as ImageSource)}
                                            options={[
                                                { value: "meta", label: "From Context (images_urls[])" },
                                                { value: "static", label: "Static URL" },
                                            ]}
                                        />
                                        {imageTextSource === "static" ? (
                                            <Input
                                                placeholder="Static image URL"
                                                value={imageTextUrl}
                                                onChange={(e) => setImageTextUrl(e.target.value)}
                                            />
                                        ) : (
                                            <Text type="secondary">
                                                Placeholder: <code>{imageTextVarPath}</code>
                                            </Text>
                                        )}
                                    </Space>
                                </div>
                                <HelpList
                                    items={[
                                        "Outside the 24-hour customer care window, WhatsApp requires an approved template.",
                                        "Cards have button limits: max 10 buttons total; up to 2 URL buttons; up to 10 quick replies; only one call type.",
                                        "If sending in-session without approval, only 3 buttons are allowed.",
                                        "If you plan to submit a card with media/variables, a valid media sample is required.",
                                    ]}
                                />
                            </>
                        ) : null}
                    </>
                ) : null}

                {layout === "text_buttons" ? (
                    <>
                        {!useStepSections || createStep === 2 ? (
                            <div className="v2-template-builder-section-box">
                                <Input.TextArea
                                    rows={4}
                                    placeholder="Body text (supports {{context.*}})"
                                    value={ctaBody}
                                    dir={direction}
                                    onChange={(e) => setUnifiedBody(e.target.value)}
                                    ref={ctaBodyRef}
                                    onFocus={() => setActiveBodyKey("cta")}
                                    onSelect={() => recordSelection("cta", ctaBodyRef)}
                                    onKeyUp={() => recordSelection("cta", ctaBodyRef)}
                                />
                                <PlaceholderPicker onInsert={insertPlaceholder} />
                                <HelpList
                                    items={[
                                        "If the body starts/ends with a variable or has two variables in a row, approval can fail.",
                                    ]}
                                />
                            </div>
                        ) : null}
                        {!useStepSections || createStep === (includesImageStep ? 4 : 3) ? (
                            <>
                                {!useStepSections ? <Divider /> : null}
                                <Text className="v2-template-builder-section-title">Actions (1–3)</Text>
                                    {ctaActions.map((a, idx) => (
                                        <div key={`cta-${idx}`} className="v2-template-builder-section-box">
                                            <Space direction="vertical" style={{ width: "100%" }}>
                                        <Select
                                            value={a.type as ActionType}
                                            onChange={(v) => {
                                                const next = [...ctaActions];
                                                next[idx] = { ...next[idx], type: v };
                                                setCtaActions(next);
                                            }}
                                            options={[
                                                { value: "url", label: "URL" },
                                                { value: "phone", label: "Phone" },
                                                { value: "copy_code", label: "Copy Code" },
                                            ]}
                                        />
                                        <Input
                                            placeholder="Label"
                                            value={a.label}
                                            onChange={(e) => {
                                                const next = [...ctaActions];
                                                next[idx] = { ...next[idx], label: e.target.value };
                                                setCtaActions(next);
                                            }}
                                        />
                                        {a.type === "url" ? (
                                            <>
                                                <Select
                                                    className="v2-template-button-url-source-select"
                                                    value={a.url_source as UrlSource}
                                                    onChange={(v) => {
                                                        const next = [...ctaActions];
                                                        next[idx] = { ...next[idx], url_source: v };
                                                        setCtaActions(next);
                                                    }}
                                                    options={[
                                                        { value: "meta", label: "From Context (buttons[].url)" },
                                                        { value: "static", label: "Static URL" },
                                                    ]}
                                                />
                                                {a.url_source === "static" ? (
                                                    <Input
                                                        placeholder="Static URL"
                                                        value={a.url}
                                                        onChange={(e) => {
                                                            const next = [...ctaActions];
                                                            next[idx] = { ...next[idx], url: e.target.value };
                                                            setCtaActions(next);
                                                        }}
                                                    />
                                                ) : (
                                                    <Text type="secondary">
                                                        Placeholder: <code>{getMetaUrlVarPath(ctaActions, idx)}</code>
                                                    </Text>
                                                )}
                                            </>
                                        ) : null}
                                        {a.type === "phone" ? (
                                            <Input
                                                placeholder="Phone number"
                                                value={a.phone}
                                                onChange={(e) => {
                                                    const next = [...ctaActions];
                                                    next[idx] = { ...next[idx], phone: e.target.value };
                                                    setCtaActions(next);
                                                }}
                                            />
                                        ) : null}
                                        {a.type === "copy_code" ? (
                                            <>
                                                <Select
                                                    value={(a.code_source as CodeSource) || "static"}
                                                    onChange={(v) => {
                                                        const next = [...ctaActions];
                                                        next[idx] = { ...next[idx], code_source: v };
                                                        setCtaActions(next);
                                                    }}
                                                    options={[
                                                        { value: "meta", label: "From Context" },
                                                        { value: "static", label: "Static Code" },
                                                    ]}
                                                />
                                                {a.code_source === "static" ? (
                                                    <Input
                                                        placeholder="Code"
                                                        value={a.code}
                                                        onChange={(e) => {
                                                            const next = [...ctaActions];
                                                            next[idx] = { ...next[idx], code: e.target.value };
                                                            setCtaActions(next);
                                                        }}
                                                    />
                                                ) : (
                                                    <Input
                                                        placeholder="Code var path (required)"
                                                        value={a.code_var_path}
                                                        onChange={(e) => {
                                                            const next = [...ctaActions];
                                                            next[idx] = { ...next[idx], code_var_path: e.target.value };
                                                            setCtaActions(next);
                                                        }}
                                                    />
                                                )}
                                            </>
                                        ) : null}
                                                <Button size="large"
                                                    danger
                                                    onClick={() => setCtaActions(ctaActions.filter((_, i) => i !== idx))}
                                                    disabled={ctaActions.length <= 1}
                                                >
                                                    Remove Action
                                                </Button>
                                            </Space>
                                        </div>
                                    ))}
                                <div className="v2-template-builder-section-box">
                                    <Button size="large"
                                        onClick={() => {
                                            if (ctaActions.length >= 3) return;
                                            setCtaActions([
                                                ...ctaActions,
                                                { type: "url", label: "", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" },
                                            ]);
                                        }}
                                        disabled={ctaActions.length >= 3}
                                    >
                                        Add Action
                                    </Button>
                                </div>
                            </>
                        ) : null}
                    </>
                ) : null}

                {layout === "image_text_buttons" ? (
                    <>
                        {!useStepSections || createStep === 2 ? (
                            <>
                                <div className="v2-template-builder-section-box">
                                    <Input
                                        placeholder="Title (optional)"
                                        value={cardTitle}
                                        dir={direction}
                                        onChange={(e) => setUnifiedTitle(e.target.value)}
                                    />
                                </div>
                                <div className="v2-template-builder-section-box">
                                    <Input.TextArea
                                        rows={4}
                                        placeholder="Body (optional, supports {{context.*}})"
                                        value={cardBody}
                                        dir={direction}
                                        onChange={(e) => setUnifiedBody(e.target.value)}
                                        ref={cardBodyRef}
                                        onFocus={() => setActiveBodyKey("card")}
                                        onSelect={() => recordSelection("card", cardBodyRef)}
                                        onKeyUp={() => recordSelection("card", cardBodyRef)}
                                    />
                                    <PlaceholderPicker onInsert={insertPlaceholder} />
                                    <HelpList
                                        items={[
                                            "If the body starts/ends with a variable or has two variables in a row, approval can fail.",
                                        ]}
                                    />
                                </div>
                            </>
                        ) : null}
                        {!useStepSections || createStep === 3 ? (
                            <>
                                {!useStepSections ? <Divider /> : null}
                                <div className="v2-template-builder-section-box">
                                    <Space direction="vertical" style={{ width: "100%" }}>
                                        <Text className="v2-template-builder-section-title">Image Source</Text>
                                        <Select
                                            value={cardImageSource}
                                            onChange={(v) => setCardImageSource(v as ImageSource)}
                                            options={[
                                                { value: "meta", label: "From Context (images_urls[])" },
                                                { value: "static", label: "Static URL" },
                                            ]}
                                        />
                                        {cardImageSource === "static" ? (
                                            <Input
                                                placeholder="Static image URL"
                                                value={cardImageUrl}
                                                onChange={(e) => setCardImageUrl(e.target.value)}
                                            />
                                        ) : (
                                            <Text type="secondary">
                                                Placeholder: <code>{cardImageVarPath}</code>
                                            </Text>
                                        )}
                                    </Space>
                                </div>
                            </>
                        ) : null}
                        {!useStepSections || createStep === 4 ? (
                            <>
                                {!useStepSections ? <Divider /> : null}
                                <Text className="v2-template-builder-section-title">Buttons (optional)</Text>
                                    {cardActions.map((a, idx) => (
                                        <div key={`card-action-${idx}`} className="v2-template-builder-section-box">
                                            <Space direction="vertical" style={{ width: "100%" }}>
                                        <Select
                                            value={a.type as ActionType}
                                            onChange={(v) => {
                                                const next = [...cardActions];
                                                next[idx] = { ...next[idx], type: v };
                                                setCardActions(next);
                                            }}
                                            options={[
                                                { value: "url", label: "URL" },
                                                { value: "phone", label: "Phone" },
                                                { value: "quick_reply", label: "Quick Reply" },
                                            ]}
                                        />
                                        <Input
                                            placeholder="Label"
                                            value={a.label}
                                            onChange={(e) => {
                                                const next = [...cardActions];
                                                next[idx] = { ...next[idx], label: e.target.value };
                                                setCardActions(next);
                                            }}
                                        />
                                        {a.type === "url" ? (
                                            <>
                                                <Select
                                                    className="v2-template-button-url-source-select"
                                                    value={a.url_source as UrlSource}
                                                    onChange={(v) => {
                                                        const next = [...cardActions];
                                                        next[idx] = { ...next[idx], url_source: v };
                                                        setCardActions(next);
                                                    }}
                                                    options={[
                                                        { value: "meta", label: "From Context (buttons[].url)" },
                                                        { value: "static", label: "Static URL" },
                                                    ]}
                                                />
                                                {a.url_source === "static" ? (
                                                    <Input
                                                        placeholder="Static URL"
                                                        value={a.url}
                                                        onChange={(e) => {
                                                            const next = [...cardActions];
                                                            next[idx] = { ...next[idx], url: e.target.value };
                                                            setCardActions(next);
                                                        }}
                                                    />
                                                ) : (
                                                    <Text type="secondary">
                                                        Placeholder: <code>{getMetaUrlVarPath(cardActions, idx)}</code>
                                                    </Text>
                                                )}
                                            </>
                                        ) : null}
                                        {a.type === "phone" ? (
                                            <Input
                                                placeholder="Phone number"
                                                value={a.phone}
                                                onChange={(e) => {
                                                    const next = [...cardActions];
                                                    next[idx] = { ...next[idx], phone: e.target.value };
                                                    setCardActions(next);
                                                }}
                                            />
                                        ) : null}
                                        {a.type === "quick_reply" ? (
                                            <Input
                                                placeholder="Quick reply id"
                                                value={a.id}
                                                onChange={(e) => {
                                                    const next = [...cardActions];
                                                    next[idx] = { ...next[idx], id: e.target.value };
                                                    setCardActions(next);
                                                }}
                                            />
                                        ) : null}
                                                <Button size="large"
                                                    danger
                                                    onClick={() => setCardActions(cardActions.filter((_, i) => i !== idx))}
                                                >
                                                    Remove Button
                                                </Button>
                                            </Space>
                                        </div>
                                    ))}
                                <div className="v2-template-builder-section-box">
                                    <Button size="large"
                                        onClick={() =>
                                            setCardActions([
                                                ...cardActions,
                                                { type: "url", label: "", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" },
                                            ])
                                        }
                                    >
                                        Add Button
                                    </Button>
                                </div>
                            </>
                        ) : null}
                    </>
                ) : null}

                {(!useStepSections || createStep === 2) && layout === "menu" ? (
                    <>
                        <div className="v2-template-builder-section-box">
                            <Input.TextArea
                                rows={3}
                                placeholder="Menu body text"
                                value={menuBody}
                                dir={direction}
                                onChange={(e) => setUnifiedBody(e.target.value)}
                                ref={menuBodyRef}
                                onFocus={() => setActiveBodyKey("menu")}
                                onSelect={() => recordSelection("menu", menuBodyRef)}
                                onKeyUp={() => recordSelection("menu", menuBodyRef)}
                            />
                            <PlaceholderPicker onInsert={insertPlaceholder} />
                        </div>
                        <div className="v2-template-builder-section-box">
                            <Input
                                placeholder="Menu button text"
                                value={menuButtonText}
                                onChange={(e) => setMenuButtonText(e.target.value)}
                            />
                        </div>
                    </>
                ) : null}

                {(!useStepSections || createStep === menuItemsStepIndex) && layout === "menu" ? (
                    <>
                        <div className="v2-template-builder-section-box v2-template-menu-items-intro-box">
                            <Text className="v2-template-builder-section-title">Items (2–10)</Text>
                            <HelpList
                                items={[
                                    "List-picker is session-only and not allowed for out-of-session approval.",
                                    "Items: 1–10 total. Item title max 24 chars; description max 72; id max 200.",
                                    "Body max length: 1024 characters.",
                                ]}
                            />
                        </div>
                        {menuItems.map((it, idx) => (
                            <div key={`menu-item-${idx}`} className="v2-template-builder-section-box">
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <Input
                                        placeholder="ID"
                                        value={it.id}
                                        onChange={(e) => {
                                            const next = [...menuItems];
                                            next[idx] = { ...next[idx], id: e.target.value };
                                            setMenuItems(next);
                                        }}
                                    />
                                    <Input
                                        placeholder="Title"
                                        value={it.title}
                                        dir={direction}
                                        onChange={(e) => {
                                            const next = [...menuItems];
                                            next[idx] = { ...next[idx], title: e.target.value };
                                            setMenuItems(next);
                                        }}
                                    />
                                    <Input
                                        placeholder="Description (optional)"
                                        value={it.description}
                                        onChange={(e) => {
                                            const next = [...menuItems];
                                            next[idx] = { ...next[idx], description: e.target.value };
                                            setMenuItems(next);
                                        }}
                                    />
                                    <Button size="large"
                                        className="v2-template-remove-item-btn"
                                        danger
                                        onClick={() => setMenuItems(menuItems.filter((_, i) => i !== idx))}
                                        disabled={menuItems.length <= 2}
                                    >
                                        Remove Item
                                    </Button>
                                </Space>
                            </div>
                        ))}
                        <div className="v2-template-builder-section-box">
                            <Button size="large"
                                onClick={() => {
                                    if (menuItems.length >= 10) return;
                                    setMenuItems([
                                        ...menuItems,
                                        { id: `item_${menuItems.length + 1}`, title: "", description: "" },
                                    ]);
                                }}
                                disabled={menuItems.length >= 10}
                            >
                                Add Item
                            </Button>
                        </div>
                    </>
                ) : null}

                {layout === "carousel" ? (
                    <>
                        {!useStepSections || createStep === 2 ? (
                            <div className="v2-template-builder-section-box">
                                <Input.TextArea
                                    rows={3}
                                    placeholder="Carousel body text (optional)"
                                    value={carouselBody}
                                    dir={direction}
                                    onChange={(e) => setUnifiedBody(e.target.value)}
                                    ref={carouselBodyRef}
                                    onFocus={() => setActiveBodyKey("carousel")}
                                    onSelect={() => recordSelection("carousel", carouselBodyRef)}
                                    onKeyUp={() => recordSelection("carousel", carouselBodyRef)}
                                />
                                <PlaceholderPicker onInsert={insertPlaceholder} />
                            </div>
                        ) : null}
                        {(!useStepSections || createStep === cardsStepIndex || (!isCarouselLayout && createStep !== 1)) ? (
                            <>
                                <div className="v2-template-builder-section-box">
                                    <Text className="v2-template-builder-section-title">Cards (2–10)</Text>
                                </div>
                                {carouselCards.map((c, idx) => (
                                    <div key={`carousel-${idx}`} className="v2-template-builder-section-box v2-template-carousel-card-section">
                                        <Space direction="vertical" style={{ width: "100%" }}>
                                            {!useStepSections || createStep === cardsStepIndex ? (
                                                <>
                                                    <Input
                                                        placeholder="Title (optional)"
                                                        value={c.title_text}
                                                        dir={direction}
                                                        onChange={(e) => {
                                                            const next = [...carouselCards];
                                                            next[idx] = { ...next[idx], title_text: e.target.value };
                                                            setCarouselCards(next);
                                                        }}
                                                    />
                                                    <Input.TextArea
                                                        rows={2}
                                                        placeholder="Body (required)"
                                                        value={c.body_text}
                                                        dir={direction}
                                                        onChange={(e) => {
                                                            const next = [...carouselCards];
                                                            next[idx] = { ...next[idx], body_text: e.target.value };
                                                            setCarouselCards(next);
                                                        }}
                                                        ref={(el) => {
                                                            carouselCardBodyRefs.current[idx] = el;
                                                        }}
                                                        onFocus={() => setActiveBodyKey(`carousel_card_${idx}`)}
                                                        onSelect={() => recordSelection(`carousel_card_${idx}`, carouselCardBodyRefs.current[idx])}
                                                        onKeyUp={() => recordSelection(`carousel_card_${idx}`, carouselCardBodyRefs.current[idx])}
                                                    />
                                                    <Select
                                                        value={c.image_source as ImageSource}
                                                        onChange={(v) => {
                                                            const next = [...carouselCards];
                                                            next[idx] = { ...next[idx], image_source: v };
                                                            setCarouselCards(next);
                                                        }}
                                                        options={[
                                                            { value: "meta", label: "Image from Context (images_urls[])" },
                                                            { value: "static", label: "Static URL" },
                                                        ]}
                                                    />
                                                    {c.image_source === "static" ? (
                                                        <Input
                                                            placeholder="Static image URL"
                                                            value={c.image_url}
                                                            onChange={(e) => {
                                                                const next = [...carouselCards];
                                                                next[idx] = { ...next[idx], image_url: e.target.value };
                                                                setCarouselCards(next);
                                                            }}
                                                        />
                                                    ) : (
                                                        <Text type="secondary">
                                                            Placeholder: <code>{`context.images_urls.${idx}`}</code>
                                                        </Text>
                                                    )}
                                                    <Divider className="v2-template-carousel-card-divider" />
                                                    <Text className="v2-template-builder-section-title">Buttons (optional)</Text>
                                                    {Array.isArray(c.actions) && c.actions.map((a: any, aIdx: number) => (
                                                        <Card key={`carousel-action-${idx}-${aIdx}`} size="small">
                                                            <Space direction="vertical" style={{ width: "100%" }}>
                                                                <Select
                                                                    value={a.type as ActionType}
                                                                    onChange={(v) => {
                                                                        const next = [...carouselCards];
                                                                        const actions = [...(next[idx].actions || [])];
                                                                        actions[aIdx] = { ...actions[aIdx], type: v };
                                                                        next[idx] = { ...next[idx], actions };
                                                                        setCarouselCards(next);
                                                                    }}
                                                                    options={[
                                                                        { value: "url", label: "URL" },
                                                                        { value: "phone", label: "Phone" },
                                                                        { value: "quick_reply", label: "Quick Reply" },
                                                                    ]}
                                                                />
                                                                <Input
                                                                    placeholder="Label"
                                                                    value={a.label}
                                                                    onChange={(e) => {
                                                                        const next = [...carouselCards];
                                                                        const actions = [...(next[idx].actions || [])];
                                                                        actions[aIdx] = { ...actions[aIdx], label: e.target.value };
                                                                        next[idx] = { ...next[idx], actions };
                                                                        setCarouselCards(next);
                                                                    }}
                                                                />
                                                                {a.type === "url" ? (
                                                                    <>
                                                                        <Select
                                                                            className="v2-template-button-url-source-select"
                                                                            value={a.url_source as UrlSource}
                                                                            onChange={(v) => {
                                                                                const next = [...carouselCards];
                                                                                const actions = [...(next[idx].actions || [])];
                                                                                actions[aIdx] = { ...actions[aIdx], url_source: v };
                                                                                next[idx] = { ...next[idx], actions };
                                                                                setCarouselCards(next);
                                                                            }}
                                                                            options={[
                                                                                { value: "meta", label: "From Context (buttons[].url)" },
                                                                                { value: "static", label: "Static URL" },
                                                                            ]}
                                                                        />
                                                                        {a.url_source === "static" ? (
                                                                            <Input
                                                                                placeholder="Static URL"
                                                                                value={a.url}
                                                                                onChange={(e) => {
                                                                                    const next = [...carouselCards];
                                                                                    const actions = [...(next[idx].actions || [])];
                                                                                    actions[aIdx] = { ...actions[aIdx], url: e.target.value };
                                                                                    next[idx] = { ...next[idx], actions };
                                                                                    setCarouselCards(next);
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <Text type="secondary">
                                                                                Placeholder: <code>{getMetaUrlVarPath(c.actions || [], aIdx)}</code>
                                                                            </Text>
                                                                        )}
                                                                    </>
                                                                ) : null}
                                                                {a.type === "phone" ? (
                                                                    <Input
                                                                        placeholder="Phone number"
                                                                        value={a.phone}
                                                                        onChange={(e) => {
                                                                            const next = [...carouselCards];
                                                                            const actions = [...(next[idx].actions || [])];
                                                                            actions[aIdx] = { ...actions[aIdx], phone: e.target.value };
                                                                            next[idx] = { ...next[idx], actions };
                                                                            setCarouselCards(next);
                                                                        }}
                                                                    />
                                                                ) : null}
                                                                {a.type === "quick_reply" ? (
                                                                    <Input
                                                                        placeholder="Quick reply id"
                                                                        value={a.id}
                                                                        onChange={(e) => {
                                                                            const next = [...carouselCards];
                                                                            const actions = [...(next[idx].actions || [])];
                                                                            actions[aIdx] = { ...actions[aIdx], id: e.target.value };
                                                                            next[idx] = { ...next[idx], actions };
                                                                            setCarouselCards(next);
                                                                        }}
                                                                    />
                                                                ) : null}
                                                                <Button
                                                                    size="large"
                                                                    className="v2-template-carousel-card-remove-btn"
                                                                    danger
                                                                    onClick={() => {
                                                                        const next = [...carouselCards];
                                                                        const actions = [...(next[idx].actions || [])].filter((_: any, i: number) => i !== aIdx);
                                                                        next[idx] = { ...next[idx], actions };
                                                                        setCarouselCards(next);
                                                                    }}
                                                                >
                                                                    Remove Button
                                                                </Button>
                                                            </Space>
                                                        </Card>
                                                    ))}
                                                    <Button
                                                        size="large"
                                                        className="v2-template-carousel-card-add-btn"
                                                        onClick={() => {
                                                            const next = [...carouselCards];
                                                            const actions = [...(next[idx].actions || [])];
                                                            actions.push({ type: "url", label: "", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" });
                                                            next[idx] = { ...next[idx], actions };
                                                            setCarouselCards(next);
                                                        }}
                                                    >
                                                        Add Button
                                                    </Button>
                                                    <Divider className="v2-template-carousel-card-divider" />
                                                    <Button
                                                        size="large"
                                                        className="v2-template-carousel-card-remove-card-btn"
                                                        danger
                                                        onClick={() => setCarouselCards(carouselCards.filter((_, i) => i !== idx))}
                                                        disabled={carouselCards.length <= 2}
                                                    >
                                                        Remove Card
                                                    </Button>
                                                </>
                                            ) : null}
                                        </Space>
                                    </div>
                                ))}
                                {!useStepSections || createStep === cardsStepIndex ? (
                                    <div className="v2-template-builder-section-box">
                                        <Button size="large"
                                            onClick={() => {
                                                if (carouselCards.length >= 10) return;
                                                setCarouselCards([
                                                    ...carouselCards,
                                                    {
                                                        title_text: "",
                                                        body_text: "",
                                                        image_source: "meta",
                                                        image_url: "",
                                                        image_var_path: `context.images_urls.${carouselCards.length}`,
                                                        actions: [{ type: "url", label: "", url_source: "meta", url: "", url_var_path: "context.buttons.0.url" }],
                                                    },
                                                ]);
                                            }}
                                            disabled={carouselCards.length >= 10}
                                        >
                                            Add Card
                                        </Button>
                                    </div>
                                ) : null}
                            </>
                        ) : null}
                    </>
                ) : null}

                {(!useStepSections || createStep === imageStepIndex) && (layout === "image_text" || layout === "image_text_buttons" || layout === "carousel") ? (
                    <>
                        {!useStepSections ? <Divider /> : null}
                        <div className="v2-template-builder-section-box">
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <Text strong className="v2-template-builder-section-title">Runtime defaults</Text>
                                <Input
                                    placeholder="Default image URL (used when images_urls[] is empty)"
                                    value={defaultImageUrl}
                                    onChange={(e) => setDefaultImageUrl(e.target.value)}
                                />
                            </Space>
                        </div>
                    </>
                ) : null}

                {(!useStepSections || createStep === buttonsStepIndex) && (layout === "text_buttons" || layout === "image_text_buttons" || layout === "carousel") ? (
                    <>
                        {!useStepSections ? <Divider /> : null}
                        <div className="v2-template-builder-section-box">
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <Text strong className="v2-template-builder-section-title">Runtime defaults</Text>
                                <Input
                                    placeholder="Default button label (used when context.buttons[].label is missing)"
                                    value={defaultButtonLabel}
                                    onChange={(e) => setDefaultButtonLabel(e.target.value)}
                                />
                            </Space>
                        </div>
                        {templateCategory === "MARKETING" ? (
                            <div className="v2-template-builder-section-box">
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <Text strong className="v2-template-builder-section-title">Opt-out footer text</Text>
                                    <Input
                                        value={optOutText}
                                        onChange={(e) => setOptOutText(e.target.value)}
                                        placeholder={DEFAULT_OPT_OUT_TEXT}
                                    />
                                </Space>
                            </div>
                        ) : null}
                    </>
                ) : null}

                {isCreateStepFlow ? (
                    <Space style={{ width: "100%", justifyContent: "space-between" }}>
                        <Button size="large"
                            onClick={() => setCreateStep((prev) => Math.max(1, prev - 1))}
                            disabled={createStep <= 1}
                        >
                            Back
                        </Button>
                        {createStep < totalWizardSteps ? (
                            <Button size="large"
                                type="primary"
                                onClick={() => setCreateStep((prev) => Math.min(totalWizardSteps, prev + 1))}
                                disabled={!canGoNext}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button size="large"
                                type="primary"
                                onClick={save}
                                loading={saving}
                                disabled={!!validationError}
                            >
                                {isEditMode ? "Save changes" : "Create template"}
                            </Button>
                        )}
                    </Space>
                ) : (
                    <Button size="large"
                        type="primary"
                        onClick={save}
                        loading={saving}
                        disabled={!!validationError}
                    >
                        Save changes
                    </Button>
                )}
                        </Space>
                    </ConfigProvider>
                </fieldset>
                ) : null}
            </Card>
    );

    if (embedded) {
        return <div className="v2-template-builder-embedded">{content}</div>;
    }

    return (
        <Page
            title={isEditMode ? "Edit Template" : "New Template"}
            subtitle="Configure content, placeholders and delivery options for V2 WhatsApp templates."
            className="v2-template-builder-page"
            backButton
        >
            {content}
        </Page>
    );
}
